import { create } from 'zustand';
import {
  readAuth,
  saveAuth,
  clearAuth,
  type AuthData,
  type AuthSubsidiary,
  type AuthBranch,
  type AuthUser,
} from '../lib/authClient';
import {
  loginRequest,
  selectSubsidiaryRequest,
  selectBranchRequest,
  AuthApiError,
} from '../lib/authApi';

// ─────────────────────────────────────────────────────────────────────────────
// Auth store (Phase 7.5).
//
// State machine:
//
//   unauthenticated ── login() ──────────► pending
//   pending ─────── chooseSubsidiary() ──► subsidiary-chosen
//   subsidiary-chosen ─ chooseBranch() ──► session
//   session ─────── logout() ────────────► unauthenticated
//
//   Any state ─── token rejected ────────► unauthenticated (with banner)
//
// The store hydrates from localStorage on creation. If a session token is
// found, we trust the stored stage on first paint — the next API call (the
// polling tick) will verify the token, and AuthRequiredError handling sends
// us back to unauthenticated if it fails.
//
// We DON'T validate the token with /me on store init. That would block the
// first paint on a network round-trip and add complexity for ~zero benefit:
// if the token's bad, the polling tick that runs immediately after mount
// will catch it within ~1 second.
// ─────────────────────────────────────────────────────────────────────────────

export type AuthState =
  | { kind: 'unauthenticated'; banner: string | null }
  | { kind: 'logging-in' }
  | { kind: 'pending'; user: AuthUser; subsidiaries: AuthSubsidiary[]; pendingToken: string }
  | { kind: 'choosing-subsidiary'; user: AuthUser; subsidiaries: AuthSubsidiary[]; pendingToken: string }
  | { kind: 'subsidiary-chosen'; user: AuthUser; subsidiary: AuthSubsidiary; branches: AuthBranch[]; pendingToken: string }
  | { kind: 'choosing-branch'; user: AuthUser; subsidiary: AuthSubsidiary; branches: AuthBranch[]; pendingToken: string }
  | { kind: 'session'; data: Extract<AuthData, { stage: 'session' }> }
  /**
   * The tenant has not finished its Initial Setup Wizard, so the server issued
   * no session. The credentials are held in memory only, to sign in again the
   * moment the wizard completes.
   */
  | {
      kind: 'setup';
      slug: string;
      username: string;
      password: string;
      tenantName: string;
      setupToken: string | null;
    };

interface AuthStore {
  state: AuthState;
  error: string | null;

  login: (tenantSlug: string, username: string, password: string) => Promise<void>;
  chooseSubsidiary: (subsidiaryId: number) => Promise<void>;
  chooseBranch: (branchId: number) => Promise<void>;
  /** The wizard finished — sign in again with the credentials it was opened with. */
  finishSetup: (owner?: { username: string; password: string }) => Promise<void>;
  /** Leave the wizard without finishing it. */
  exitSetup: () => void;
  backToSubsidiaryPicker: () => void;
  logout: (banner?: string) => void;
  /** Adopt a cashier JWT handed over from the till (SSO). See below. */
  adoptCashierToken: (token: string) => boolean;

  /** Called by data layers when an API call returns 401. */
  onAuthExpired: (banner?: string) => void;

  /** Reset the displayed error after the user dismisses it. */
  clearError: () => void;
}

function initialStateFromStorage(): AuthState {
  const stored = readAuth();
  if (!stored) return { kind: 'unauthenticated', banner: null };
  if (stored.stage === 'session') {
    return { kind: 'session', data: stored };
  }
  // Pending and subsidiary-chosen stages are NOT restored from storage on
  // boot — pending tokens are short-lived (5 min) and the user shouldn't
  // wake up halfway through a multi-step flow after a long absence.
  // Wipe them and start fresh.
  clearAuth();
  return { kind: 'unauthenticated', banner: null };
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  state: initialStateFromStorage(),
  error: null,

  login: async (tenantSlug, username, password) => {
    set({ state: { kind: 'logging-in' }, error: null });
    try {
      const result = await loginRequest({
        tenant_slug: tenantSlug,
        username,
        password,
      });
      // Auto-skip subsidiary picker if user has exactly one subsidiary.
      if (result.available_subsidiaries.length === 1) {
        const onlySubsidiary = result.available_subsidiaries[0]!;
        set({
          state: {
            kind: 'choosing-subsidiary',
            user: result.user,
            subsidiaries: result.available_subsidiaries,
            pendingToken: result.pending_token,
          },
        });
        // Fire-and-forget the next step. If it fails we surface error inline.
        await get().chooseSubsidiary(onlySubsidiary.id);
        return;
      }
      set({
        state: {
          kind: 'pending',
          user: result.user,
          subsidiaries: result.available_subsidiaries,
          pendingToken: result.pending_token,
        },
      });
      saveAuth({
        stage: 'pending',
        token: result.pending_token,
        saved_at: Date.now(),
        user: result.user,
        available_subsidiaries: result.available_subsidiaries,
      });
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'SETUP_REQUIRED') {
        const b = err.body ?? {};
        set({
          state: {
            kind: 'setup',
            slug: tenantSlug,
            username,
            password,
            tenantName: typeof b.tenant_name === 'string' ? b.tenant_name : tenantSlug,
            setupToken: typeof b.setup_token === 'string' ? b.setup_token : null,
          },
          error: null,
        });
        return;
      }
      set({
        state: { kind: 'unauthenticated', banner: null },
        error: messageForError(err),
      });
    }
  },

  finishSetup: async (owner) => {
    const s = get().state;
    if (s.kind !== 'setup') return;
    await get().login(s.slug, owner?.username ?? s.username, owner?.password ?? s.password);
  },

  exitSetup: () => set({ state: { kind: 'unauthenticated', banner: null }, error: null }),

  chooseSubsidiary: async (subsidiaryId) => {
    const s = get().state;
    if (s.kind !== 'pending' && s.kind !== 'choosing-subsidiary') {
      return; // ignore stray clicks from outside the right stage
    }
    set({
      state: {
        kind: 'choosing-subsidiary',
        user: s.user,
        subsidiaries: s.subsidiaries,
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    try {
      const result = await selectSubsidiaryRequest(s.pendingToken, subsidiaryId);
      // Auto-skip branch picker if subsidiary has exactly one branch.
      if (result.available_branches.length === 1) {
        const onlyBranch = result.available_branches[0]!;
        set({
          state: {
            kind: 'subsidiary-chosen',
            user: s.user,
            subsidiary: result.subsidiary,
            branches: result.available_branches,
            pendingToken: result.pending_token,
          },
        });
        await get().chooseBranch(onlyBranch.id);
        return;
      }
      set({
        state: {
          kind: 'subsidiary-chosen',
          user: s.user,
          subsidiary: result.subsidiary,
          branches: result.available_branches,
          pendingToken: result.pending_token,
        },
      });
      saveAuth({
        stage: 'subsidiary-chosen',
        token: result.pending_token,
        saved_at: Date.now(),
        user: s.user,
        subsidiary: result.subsidiary,
        available_branches: result.available_branches,
      });
    } catch (err) {
      // On 401 (token expired or rejected), restart the flow.
      if (err instanceof AuthApiError && err.status === 401) {
        get().onAuthExpired('Your sign-in expired. Please log in again.');
        return;
      }
      // For other errors, surface inline and bring the user back to the
      // subsidiary picker so they can try a different one.
      set({
        state: {
          kind: 'pending',
          user: s.user,
          subsidiaries: s.subsidiaries,
          pendingToken: s.pendingToken,
        },
        error: messageForError(err),
      });
    }
  },

  chooseBranch: async (branchId) => {
    const s = get().state;
    if (s.kind !== 'subsidiary-chosen' && s.kind !== 'choosing-branch') {
      return;
    }
    set({
      state: {
        kind: 'choosing-branch',
        user: s.user,
        subsidiary: s.subsidiary,
        branches: s.branches,
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    try {
      const result = await selectBranchRequest(s.pendingToken, branchId);
      const branch = s.branches.find((b) => b.id === result.session.branch_id);
      // Defensive: branch should always be in s.branches since we validated
      // server-side, but if somehow not, fabricate a minimal entry from the
      // session info so we don't render an undefined screen.
      const branchOut: AuthBranch = branch ?? {
        id: result.session.branch_id,
        code: '',
        name: result.session.branch_name,
        subsidiary_id: result.session.subsidiary_id,
        has_cash_drawer: result.session.cash_drawer_id !== null,
      };
      const data: Extract<AuthData, { stage: 'session' }> = {
        stage: 'session',
        token: result.token,
        saved_at: Date.now(),
        user: s.user,
        subsidiary: s.subsidiary,
        branch: branchOut,
        session: result.session,
      };
      saveAuth(data);
      set({ state: { kind: 'session', data } });
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 401) {
        get().onAuthExpired('Your sign-in expired. Please log in again.');
        return;
      }
      // 409 WRONG_SUBSIDIARY: shouldn't happen in normal flow (we only
      // surface branches that match the subsidiary), but if it does the
      // user gets shoved back to the picker with an explanation.
      set({
        state: {
          kind: 'subsidiary-chosen',
          user: s.user,
          subsidiary: s.subsidiary,
          branches: s.branches,
          pendingToken: s.pendingToken,
        },
        error: messageForError(err),
      });
    }
  },

  backToSubsidiaryPicker: () => {
    const s = get().state;
    if (s.kind !== 'subsidiary-chosen') return;
    // We can't roll back the server-side pendingToken's subsidiary_id claim
    // (it's signed in). The cleanest move is to ask the user to pick a
    // subsidiary again — the existing pending token's subsidiary_id will
    // be overwritten on the next /select-subsidiary call.
    set({
      state: {
        kind: 'pending',
        user: s.user,
        subsidiaries: [], // we no longer have the list at this stage
        pendingToken: s.pendingToken,
      },
      error: null,
    });
    // Note: subsidiaries list is empty here. The picker screen handles
    // this by re-fetching via /login if the list is empty. In practice,
    // we hit this only when a user picks a subsidiary with 0 branches and
    // wants to go back — Phase 7.6 may add a smarter "re-fetch" path.
  },

  logout: (banner) => {
    clearAuth();
    set({
      state: {
        kind: 'unauthenticated',
        banner: banner ?? null,
      },
      error: null,
    });
  },

  onAuthExpired: (banner) => {
    clearAuth();
    set({
      state: {
        kind: 'unauthenticated',
        banner: banner ?? 'Your sign-in expired. Please log in again.',
      },
      error: null,
    });
  },

  /**
   * Adopt a cashier session JWT handed over from the till.
   *
   * The cashier opens this app with #sso=<jwt>; that token is the SAME one the
   * till uses, and every endpoint this app calls (/api/v1/kds/**, and the
   * legacy /api/v1/service/**) authenticates with `fastify.cashierAuth`, which
   * accepts it as-is. So the handoff needs no server round trip at all: the
   * claims this app displays — user, branch, subsidiary, tenant — are already
   * inside the token.
   *
   * Note we do NOT call the service app's own /me to hydrate. That endpoint
   * requires a 'service:session'-scoped token minted by this app's own login
   * flow, which a cashier JWT is not. Trying to reuse it here would 401 on a
   * perfectly valid token and bounce the user to a second login — the exact
   * thing the button exists to avoid.
   *
   * The token is not verified locally (we cannot; the signing key is
   * server-side). It does not need to be: the first API call either works or
   * returns 401, and the 401 path already routes back to the login screen.
   *
   * Returns false when the token is unreadable, so the caller can fall through
   * to the normal login screen rather than land on a broken session.
   */
  adoptCashierToken: (token) => {
    const claims = decodeJwtPayload(token);
    if (!claims || !claims.tenant_db || !claims.sub) return false;

    const subsidiaryId = Number(claims.subsidiary_id ?? 0);
    const branchId = Number(claims.branch_id ?? 0);
    if (!subsidiaryId || !branchId) return false;

    const username = String(claims.username ?? '');
    const data: Extract<AuthData, { stage: 'session' }> = {
      stage: 'session',
      token,
      saved_at: Date.now(),
      user: {
        id: Number(claims.sub),
        username,
        name: username,
        name_ar: null,
        tenant_slug: String(claims.tenant_slug ?? ''),
      },
      // Codes and display names are not in the cashier JWT. The KDS bootstrap
      // returns the real branch name and overwrites the header with it a
      // moment later; until then the id is a truthful placeholder rather than
      // an invented name.
      subsidiary: { id: subsidiaryId, code: '', name: String(claims.subsidiary_name ?? '') },
      branch: {
        id: branchId,
        code: '',
        name: String(claims.branch_name ?? ''),
        subsidiary_id: subsidiaryId,
        has_cash_drawer: claims.cash_drawer_id != null,
      },
      session: {
        user_id: Number(claims.sub),
        username,
        tenant_db: String(claims.tenant_db),
        tenant_slug: String(claims.tenant_slug ?? ''),
        subsidiary_id: subsidiaryId,
        subsidiary_name: String(claims.subsidiary_name ?? ''),
        branch_id: branchId,
        branch_name: String(claims.branch_name ?? ''),
        cash_drawer_id: claims.cash_drawer_id == null ? null : Number(claims.cash_drawer_id),
      },
    };

    saveAuth(data);
    set({ state: { kind: 'session', data }, error: null });
    return true;
  },

  clearError: () => {
    set({ error: null });
  },
}));

// ── helpers ─────────────────────────────────────────────────────────────────

function messageForError(err: unknown): string {
  if (err instanceof AuthApiError) {
    return mapErrorCode(err.code);
  }
  if (err instanceof Error) {
    return `Network error: ${err.message}`;
  }
  return 'Unexpected error. Please try again.';
}

/**
 * Map a backend error code to a user-facing message.
 * Anything we don't recognise gets a generic safe message.
 */
function mapErrorCode(code: string): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return 'Invalid username or password.';
    case 'ACCOUNT_INACTIVE':
      return 'This account is not activated. Contact your administrator.';
    case 'ACCOUNT_LOCKED':
      return 'This account is temporarily locked. Try again later.';
    case 'NO_COMPANY_ACCESS':
      return 'This account has no company access. Contact your administrator.';
    case 'TENANT_NOT_FOUND':
      return 'Unknown tenant. Check the tenant name and try again.';
    case 'FORBIDDEN_SUBSIDIARY':
      return 'You no longer have access to this company. Try another.';
    case 'FORBIDDEN_BRANCH':
      return 'You no longer have access to this branch. Try another.';
    case 'WRONG_SUBSIDIARY':
      return 'This branch is not part of the chosen company.';
    case 'BRANCH_NOT_FOUND':
      return 'Branch not found. It may have been deleted.';
    case 'SUBSIDIARY_NOT_SELECTED':
      return 'Please choose a company first.';
    case 'INVALID_TOKEN':
      return 'Your sign-in expired. Please log in again.';
    case 'DB_UNAVAILABLE':
      return 'The server is temporarily unavailable. Please try again in a moment.';
    case 'INVALID_REQUEST':
      return 'Some required fields are missing or invalid.';
    default:
      return `Sign-in failed (${code}).`;
  }
}

/**
 * Read a JWT's payload without verifying it.
 *
 * Verification is the server's job and cannot be done here — the HS256 secret
 * is server-side, and shipping it to a browser to "check" a token would be
 * strictly worse than not checking. All this does is read claims we then use
 * for display, from a token the server will independently accept or reject on
 * the very next request.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    // base64url → base64, then pad. atob rejects the URL-safe alphabet.
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
